import { useState, useEffect, useRef, useMemo } from "react";
import Dropdown from "../../../components/Dropdown";
import { useSelector, useDispatch } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import DateRangePicker from "../../../components/DateRangePicker";
import { selectChartSelectedTime, fetchCompanyChart, setSeletectedDateForFilter } from "../../../reducers/companyHome";
import { CompanyDashboardMemberData, CompanyDashboardChartData } from "../../../api/types.gen";
import { BarPlot, ChartsTooltip } from '@mui/x-charts';
import { ChartContainer } from '@mui/x-charts/ChartContainer';
import { Box, Typography } from '@mui/material';
import {
  LinePlot,
  MarkPlot,
  lineElementClasses,
  markElementClasses,
} from '@mui/x-charts/LineChart';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import "./CompanyHomeChart.css";
import { formatXAxisLabel, formatXAxisTooltip, getMarkElementStyle, calculateOptimalStep } from "../../../utils/formatChart";
import { FILTER_TIME_OPTIONS, MAX_X_CHART_LABELS } from "../../../constants/index"

type memberInfoExcerptProps = {
  data: CompanyDashboardMemberData;
  idx: number;
}

const MemberRegisterInfo = ({ data, idx }: memberInfoExcerptProps) => {
  return (
    <div className="rounded-lg">
      <span className="text-sm flex items-center gap-1 font-bold leading-[1.125rem] text-center alu-text-base-1">
        {data?.type === 'line' ? (
        <span className="relative flex items-center justify-center w-[21px] h-3">
          <span 
            style={{ backgroundColor: `#${data.bgColor}` }} 
            className="w-full h-[1px]"
          ></span>
          <span 
            style={{ backgroundColor: `#${data.bgColor}` }} 
            className="absolute w-[7px] h-[7px] rounded-full"
          ></span>
        </span>
      ) : (
        <span
          style={{ backgroundColor: `#${data.bgColor}` }}
          className={`w-3 h-3 rounded`}
        ></span>        
      )}
      {data.title}
      </span>
      <span className="flex justify-center items-baseline alu-text-base-2">
        <strong className="text-[2rem] font-bold leading-[3rem] tracking-wide">
          {data.quantity}
          <span className="text-xl font-bold leading-[1.125rem] tracking-tight">
            {idx === 0 ? "人" : "件"}
          </span>
        </strong>
      </span>
    </div>
  )
}

const TalentChartContainer = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const [width, setWidth] = useState(1000);
  const chartRef = useRef(null);
  const chartData: CompanyDashboardChartData = useSelector((state: any) => state.companyHome.chartData);
  const numberOfRegistrations = chartData.total_register ?? []
  const numberOfAccesses = chartData.total_accesses ?? []
  const jobChangeFlag = chartData.job_change_flag ?? []
  const sumJobChangeFlag = jobChangeFlag.reduce((acc, val) => acc + val, 0);
  const labels = chartData.labels ?? []
  const chartSelectedTime = useSelector((state: any) => state.companyHome.chartSelectedTime);

  type ChartType = {
    type: 'bar' | 'line',
    data: number[],
    label: string,
    stack?: string,
    color: string,
    yAxisKey: 'left' | 'right',
    curve?: 'linear' | 'monotoneX' | 'step' | 'natural',
  }

  useEffect(() => {
    const updateWidth = () => {
      if (chartRef.current) {
        setWidth(chartRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);
  
  let seriesChart: ChartType[] = [
    { id: 'access', type: 'bar', data: numberOfAccesses, label: '総アクセス数', stack: 'stack1', color: '#009CF2', yAxisKey: 'right', },
    { id: 'register', type: 'line', data: numberOfRegistrations, label: '登録人数', color: '#1D70D4', yAxisKey: 'left', curve: 'linear', },
    { id: 'jobChange', type: 'line', data: jobChangeFlag, label: '転職フラグ', color: '#FF861A', yAxisKey: 'left', curve: 'linear', },    
  ]

  const safeMax = (arr: (number | null | undefined)[] = []) => {
    const nums = arr.filter((v): v is number => typeof v === 'number');
    return nums.length > 0 ? Math.max(...nums) : 0;
  };

  const leftMax = safeMax([
    ...numberOfRegistrations,
    ...(jobChangeFlag ?? []),
  ]);

  const rightMax = safeMax(numberOfAccesses);

  const leftYAxis =
    leftMax === 0
      ? {
          min: 0,
          max: 1,
          interval: 1,
          alignTicks: true,
        }
      : {
          min: 0,
          alignTicks: true,
        };

const rightYAxis =
  rightMax === 0
    ? {
        min: 0,
        max: 1,
        interval: 1,
        alignTicks: true,
      }
    : {
        min: 0,
        alignTicks: true,
      };

  const getSpaceForYAxis = (maxValue: number) => {
    const spaceNumber = chartSelectedTime?.id == FILTER_TIME_OPTIONS.LAST_YEAR ? 20 : 14
    return maxValue.toString().length * 8 + parseInt(spaceNumber);
  };

  const maxRightValue = Math.max(...numberOfAccesses);
  const dynamicRightMargin = getSpaceForYAxis(maxRightValue);

  const maxLeftValue = Math.max(leftMax, sumJobChangeFlag);
  const dynamicLeftMargin = getSpaceForYAxis(maxLeftValue);

  const getStrict20pxGap = (chartWidth, labelsCount, dynamicLeftMargin, dynamicRightMargin, targetBarWidth = 20) => { // targetBarWidth with maximum 20px
    if (!chartWidth || labelsCount === 0) return 0.5;
    if (labelsCount <= 2) return 0.98;
    if (labelsCount <= 4) return 0.94;
    if (labelsCount <= 8) return 0.9;    

    const totalMargin = Number(dynamicLeftMargin) + Number(dynamicRightMargin);
    const drawingWidth = chartWidth - totalMargin;
    const bandWidth = drawingWidth / labelsCount;

    if (bandWidth <= targetBarWidth) return 0.1;
    
    const gapRatio = (bandWidth - targetBarWidth) / bandWidth;
    
    return Math.min(0.75, gapRatio);
  };

  const step = calculateOptimalStep(width, labels?.length, chartSelectedTime?.id);
  const visibleLabels = labels.filter((value, index) => {
    const isEveryStep = index % step === 0;
    const isLastItem = index === labels.length - 1;
    const isTooCloseToLast = (labels.length - 1) - index < step / 2;

    if (isLastItem) return true;
    if (isEveryStep && !isTooCloseToLast) return true;
    
    return false;
  });
  const checkIsToClose = (step, totalLabels) => {
    const lastIdx = totalLabels - 1;
    const secondToLastVisibleIdx = Math.floor((lastIdx - 1) / step) * step;
    const isTooClose = (lastIdx - secondToLastVisibleIdx) < (step * 0.8);
    return isTooClose
  }

  const shouldHideMiddleMarks = labels.length > 90; // Hide dots over 90 labels

  return (
    <Box sx={{ width: '100%', position: 'relative' }} ref={chartRef}>
      <ChartContainer
        className="line-chart-talent-login-register"
        width={width}
        height={300}
        series={seriesChart}
        margin={{ 
          left: dynamicLeftMargin, 
          right: dynamicRightMargin,
        }}
        //@ts-ignore
        xAxis={[{
          data: labels,
          scaleType: 'band',
          categoryGapRatio: getStrict20pxGap(width, labels.length, dynamicLeftMargin, dynamicRightMargin),
          barGapRatio: 0, 
          minTickGap: 0,
          tickInterval: (value, index) => visibleLabels.includes(value),
          tickLabelInterval: (value, index) => visibleLabels.includes(value),
          valueFormatter: (value, context) => {
            const index = labels.indexOf(value);
            if (context?.location === 'tick') {
              return formatXAxisLabel(value, chartSelectedTime?.id, visibleLabels);
            }            
            return formatXAxisTooltip(value, index, chartSelectedTime?.id);
          }
        }]}
        yAxis={[
          {
            id: 'left',
            scaleType: 'linear',
            disableLine: true,
            disableTicks: true,
            tickMinStep: 1,
            valueFormatter: (value, context) => {
              if (!context || context.location === 'tooltip') {
                return value !== null ? Math.round(value) : '';
              }

              if (leftMax === 0 && value === 1) return ''

              if (value >= 1000000) return `${(value / 1000000).toFixed(0)}M`;
              if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
              return value;
            },
            ...leftYAxis,
          },
          {
            id: 'right',
            scaleType: 'linear',
            position: 'right',
            disableLine: false,
            disableTicks: false,
            tickMinStep: 1,
            valueFormatter: (value, context) => {
              if (!context || context.location === 'tooltip') {
                return value !== null ? Math.round(value) : '';
              }

              if (rightMax === 0 && value === 1) return ''

              if (value >= 1000000) return `${(value / 1000000).toFixed(0)}M`;
              if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
              return value;
            },
            ...rightYAxis,
          },
        ]}
        sx={{
          '& .MuiMarkElement-series-register': getMarkElementStyle('#1D70D4', labels.length, shouldHideMiddleMarks),
          '& .MuiMarkElement-series-jobChange': getMarkElementStyle('#FF861A', labels.length, shouldHideMiddleMarks),
          '& .MuiLineElement-root, .MuiMarkElement-root': { strokeWidth: '1' },
          ...(checkIsToClose(step, labels?.length) && {
            '& .MuiChartsAxis-bottom .MuiChartsAxis-tickContainer:nth-last-child(2) .MuiChartsAxis-tickLabel': {
              transform: 'translateX(10px) !important',
            },
          }),
        }}
      >
        <BarPlot />
        <LinePlot />
        <MarkPlot />
        <ChartsXAxis />
        <ChartsYAxis axisId="left" />
        <ChartsYAxis axisId="right" position="right" />
        <ChartsTooltip />
      </ChartContainer>
    </Box>
  )
}

const CompanyHomeChart = () => {
  const memberRegisteredInfo = useSelector((state: any) => state.companyHome.memberRegisteredInfo);
  const chartSelectedTime = useSelector((state: any) => state.companyHome.chartSelectedTime);
  const rankAOnly = useSelector((state: any) => state.companyHome.rankAOnly);
  const optionTimes = useSelector((state: any) => state.companyHome.optionTimes);
  const selectedTypeOfTalent = useSelector((state: any) => state.companyHome.selectedTypeOfTalent);
  const specialOptions = [6, 7];

  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

  const handleSelectTime = (value: any) => {
    dispatch(selectChartSelectedTime(value));
    return;
  };

  useEffect(() => {
    dispatch(fetchCompanyChart({
      rankAonly: rankAOnly ? 1 : 0,
      time: chartSelectedTime.id,
      talentType: selectedTypeOfTalent?.value,
    }));
  }, [chartSelectedTime, rankAOnly, selectedTypeOfTalent]);

  return (
    <>
      <div className="mb-6 rounded-2xl bg-white p-6 alu-section-box-shadow">
        <div className="flex gap-10">
          <div className="flex gap-[26px] items-center">
            {
              memberRegisteredInfo.map((data: CompanyDashboardMemberData, idx: number) => (
                <MemberRegisterInfo key={idx} idx={idx} data={data} />
              ))
            }
          </div>
          <div className="shrink-0 min-w-[10rem]">
            <Dropdown
              inputWidth="full"
              dropdownPosition="right"
              optionsAnchor="right"
              fontWeight="bold"
              type={"text"}
              options={optionTimes}
              value={chartSelectedTime}
              onChange={(value: any) => handleSelectTime(value)}
            />
          </div>
        </div>

        <div className="h-[280px] bg-[#FFF]">
          <TalentChartContainer />
        </div>
      </div>
    </>
  );
}

export default CompanyHomeChart;